import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import type { Project } from "./schema";
export const fontFaceSchema = z
  .object({
    id: z.string().regex(/^font_[a-f0-9]{64}$/),
    family: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    postscriptName: z.string().max(200),
    weight: z.number().int().min(1).max(1000),
    style: z.enum(["normal", "italic"]),
    mime: z.enum(["font/ttf", "font/otf", "font/woff", "font/woff2"]),
    bytes: z.number().int().positive().max(64000000),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    objectKey: z.string().regex(/^[a-f0-9]{64}\.(ttf|otf|woff|woff2)$/),
    embedding: z.enum(["installable", "editable", "preview-print"]),
    ascent: z.number().min(0).max(5),
    descent: z.number().min(-5).max(0),
  })
  .strict()
  .refine(
    (face) =>
      face.id === `font_${face.sha256}` &&
      face.objectKey === `${face.sha256}.${face.mime.split("/")[1]}`,
    "Font identity does not match its immutable content",
  );
export type FontFaceDescriptor = z.infer<typeof fontFaceSchema>;
const kerningCharacter = z
  .string()
  .min(1)
  .max(2)
  .refine((value) => {
    const cp = value.codePointAt(0)!;
    return (
      Array.from(value).length === 1 &&
      !/[\s\p{Cc}\p{Cf}]/u.test(value) &&
      cp >= 32 &&
      (cp < 0xd800 || cp > 0xdfff)
    );
  }, "Use one printable character");
export const kerningPairSchema = z
  .object({
    left: kerningCharacter,
    right: kerningCharacter,
    adjustment: z.number().min(-1000).max(1000),
    fontFaceId: z
      .string()
      .regex(/^font_[a-f0-9]{64}$/)
      .optional(),
  })
  .strict();
export const kerningPairsSchema = z
  .array(kerningPairSchema)
  .max(128)
  .refine(
    (pairs) =>
      new Set(
        pairs.map((pair) =>
          JSON.stringify([pair.left, pair.right, pair.fontFaceId]),
        ),
      ).size === pairs.length,
    "Each pair and font combination must be unique",
  );
export type KerningPair = z.infer<typeof kerningPairSchema>;
export const textFeaturesSchema = z
  .object({
    kerning: z.boolean().optional(),
    ligatures: z.boolean().optional(),
    pairAdjustments: kerningPairsSchema.optional(),
  })
  .strict();
export type TextFeatures = z.infer<typeof textFeaturesSchema>;
/** Extra advances in 1/1000 em, applied only at separate glyph boundaries. */
export function kerningPairDeltas(
  glyphs: readonly { codePoints?: readonly number[] }[],
  features?: TextFeatures,
  fontFaceId?: string,
): number[] {
  const pairs = features?.pairAdjustments;
  if (!pairs?.length) return [];
  const values = new Map<string, number>();
  for (const pair of pairs)
    if (!pair.fontFaceId)
      values.set(pair.left + "\0" + pair.right, pair.adjustment);
  for (const pair of pairs)
    if (pair.fontFaceId && pair.fontFaceId === fontFaceId)
      values.set(pair.left + "\0" + pair.right, pair.adjustment);
  return glyphs.map((glyph, index) => {
    const next = glyphs[index + 1];
    if (glyph.codePoints?.length !== 1 || next?.codePoints?.length !== 1)
      return 0;
    return (
      values.get(
        String.fromCodePoint(glyph.codePoints[0]) +
          "\0" +
          String.fromCodePoint(next.codePoints[0]),
      ) || 0
    );
  });
}
/** Return a fresh object: fontkit adds its defaults to the feature argument. */
export function openTypeFeatures(features?: TextFeatures) {
  if (!features || !Object.keys(features).length) return undefined;
  const settings = {
    ...(features.kerning === undefined ? {} : { kern: features.kerning }),
    ...(features.ligatures === undefined
      ? {}
      : {
          liga: features.ligatures,
          clig: features.ligatures,
          dlig: features.ligatures,
          hlig: features.ligatures,
        }),
  };
  return Object.keys(settings).length ? settings : undefined;
}
export function textFeatureCss(features?: TextFeatures): string | undefined {
  const settings = openTypeFeatures(features);
  return (
    settings &&
    Object.entries(settings)
      .map(([tag, enabled]) => `"${tag}" ${enabled ? 1 : 0}`)
      .join(", ")
  );
}
export interface FontRuntime {
  ascent: number;
  measure: (text: string, features?: TextFeatures) => number;
  hasGlyph: (codePoint: number) => boolean;
  outline?: (
    text: string,
    features?: TextFeatures,
  ) => { path: string; x: number; y: number; scale: number }[];
  underlinePosition?: number;
  underlineThickness?: number;
}
export type FontRuntimeMap = ReadonlyMap<string, FontRuntime>;
export interface FontContext {
  face: FontFaceDescriptor;
  manifest: FontFaceDescriptor[];
  fonts: FontRuntimeMap;
}
export const bytesHash = (bytes: Uint8Array) =>
  Array.from(sha256(bytes), (v) => v.toString(16).padStart(2, "0")).join("");
export function verifyFontBytes(face: FontFaceDescriptor, bytes: Uint8Array) {
  fontFaceSchema.parse(face);
  if (bytes.length !== face.bytes || bytesHash(bytes) !== face.sha256)
    throw new Error(`Font integrity check failed: ${face.name}`);
}
export const fontCssFamily = (face: FontFaceDescriptor) => `Proof_${face.id}`;
export function fontManifestDigest(manifest: FontFaceDescriptor[]) {
  return bytesHash(
    new TextEncoder().encode(
      JSON.stringify(
        [...manifest]
          .sort((a, b) => a.id.localeCompare(b.id))
          .map((f) => fontFaceSchema.parse(f)),
      ),
    ),
  );
}
export function fontVariant(
  context: FontContext,
  bold?: boolean,
  italic?: boolean,
) {
  const weight = bold
      ? Math.max(700, context.face.weight)
      : context.face.weight,
    style = italic ? "italic" : context.face.style;
  const face =
    weight === context.face.weight && style === context.face.style
      ? context.face
      : context.manifest.find(
          (f) =>
            f.family === context.face.family &&
            f.weight === weight &&
            f.style === style,
        );
  if (!face)
    throw new Error(
      `Missing managed font variant: ${context.face.family}, ${weight}, ${style}`,
    );
  const runtime = context.fonts.get(face.id);
  if (!runtime) throw new Error(`Managed font is not loaded: ${face.name}`);
  return { face, runtime };
}
export function snapshotFont(
  project: Project,
  face: FontFaceDescriptor,
  catalog: FontFaceDescriptor[],
): Project {
  const manifest = new Map(project.fontManifest.map((f) => [f.id, f]));
  for (const f of catalog.filter((f) => f.family === face.family))
    if (!manifest.has(f.id)) manifest.set(f.id, fontFaceSchema.parse(f));
  manifest.set(face.id, fontFaceSchema.parse(face));
  if (manifest.size > 100)
    throw new Error("Document exceeds 100 managed font faces");
  return { ...project, fontManifest: [...manifest.values()] };
}
